import { thetaApi, dataOf, pollableOf, pollablePageOf, MAX_PAGE_LIMIT, pageLimit } from "./theta";
import type {
  Comment,
  CreateCommentInput,
  EntityId,
  MarkPostsReadResult,
  Page,
  Poll,
  Pollable,
  Post,
  UpdatePostInput,
} from "@theta/api-client";

/**
 * One post, with `bodyMarkdown` when the viewer may edit it (so the editor
 * needs no second read).
 *
 * POLLABLE (§1.10). Pass the previous `state` as `ifChanged` and an unchanged
 * tick answers with no document at all — which is what the post detail page's
 * 1 Hz poll gets back, instead of the whole post, for as long as nobody
 * touches it.
 */
export function getPost(
  postId: number,
  init: { ifChanged?: string | undefined } = {},
): Promise<Pollable<Post | null>> {
  return thetaApi.http
    .GET("/posts/{id}", {
      params: {
        path: { id: postId },
        query: init.ifChanged ? { ifChanged: init.ifChanged } : {},
      },
    })
    .then(pollableOf<Post | null>);
}

/**
 * Edit a post or a comment.
 *
 * `attachments` are ROWS, in display order (§1.12) — the markdown token round
 * trip is gone in both directions, so an edit that reorders images sends the
 * new order rather than rewriting `![a](upload://…)` lines and hoping the
 * server's regex agrees with the client's.
 *
 * Omitted fields are left alone; sending `attachments: []` clears them.
 */
export function updatePost(postId: number, body: UpdatePostInput): Promise<Post> {
  return thetaApi.http
    .PATCH("/posts/{id}", { params: { path: { id: postId } }, body })
    .then(dataOf<Post>);
}

/**
 * `ruleId` cites the group rule the content broke (OSO-663) — in the QUERY,
 * because a DELETE body is the thing intermediaries drop. A moderator
 * deleting somebody else's content notifies the author either way; the
 * citation makes the notification say WHICH rule.
 */
export async function deletePost(
  postId: number,
  init: { ruleId?: EntityId<"groupRule"> | null } = {},
): Promise<void> {
  await thetaApi.http
    .DELETE("/posts/{id}", {
      params: {
        path: { id: postId },
        query: init.ruleId != null ? { ruleId: init.ruleId } : {},
      },
    })
    .then(dataOf<null>);
}

/**
 * Moderator hide. Reversible through `unhidePost` — for a long time it was
 * not (OSO-378). The author is notified (OSO-663); an optional `ruleId`
 * makes the notification name the rule, while `reason` stays audit-only.
 */
export function hidePost(
  postId: number,
  init: { reason?: string; ruleId?: EntityId<"groupRule"> | null } = {},
): Promise<Post> {
  return thetaApi.http
    .POST("/posts/{id}/hide", {
      params: { path: { id: postId } },
      body: { reason: init.reason ?? null, ruleId: init.ruleId ?? null },
    })
    .then(dataOf<Post>);
}

/** The mirror of `hidePost`, offered on `can.unhide` — a moderator, on a post that IS hidden. */
export function unhidePost(postId: number): Promise<Post> {
  return thetaApi.http
    .POST("/posts/{id}/unhide", {
      params: { path: { id: postId } },
      body: { reason: null },
    })
    .then(dataOf<Post>);
}

/**
 * The write half of the unread-comment highlights (OSO-622).
 *
 * ONE BATCH, FIRE-AND-FORGET. Dwell marks several cards per flush, so the
 * route takes marks in bulk (`POST /notifications/read`-shaped) and answers
 * per id — a card deleted mid-batch is a per-id `not_found` RESULT, never a
 * 404 that rolls back the rest. `seenAt` is a SERVER-issued timestamp echoed
 * back (the newest content instant the card actually rendered), never
 * `Date.now()`: a client clock is not trusted, and marking with "now" would
 * swallow a comment created between payload build and flush.
 *
 * Callers go through `lib/hooks/usePostReads.ts`, which owns the dedupe
 * ledger and the ~2 s flush — and which deliberately invalidates NOTHING on
 * success: the highlights are frozen per pageview (`useUnreadSnapshot`), the
 * feed fingerprints deliberately ignore the mark (backend A4), so nothing on
 * screen wants to react to this write.
 */
export function markPostsRead(
  marks: { id: number; seenAt: string }[],
): Promise<MarkPostsReadResult> {
  return thetaApi.http
    .POST("/posts/read", {
      body: {
        // The one cast: the ids come off `PostCard.id`, which IS the branded
        // `EntityId<"post">`, but they travel through the ledger's number-keyed
        // Map and come back unbranded.
        marks: marks.map((m) => ({ id: m.id as EntityId<"post">, seenAt: m.seenAt })),
      },
    })
    .then(dataOf<MarkPostsReadResult>);
}

/**
 * One page of a post's comment thread — or of ONE comment's replies.
 *
 * DEPTH-FIRST, so a reply always follows what it replies to and a client
 * indents by `depth` instead of rebuilding a tree from ids. That single
 * property is what deletes `buildCommentTree`, `findRootPN` and the whole
 * `reply_to_post_number` -> `post_number` matching behind them: post NUMBERS
 * do not exist in v1 (§1.2/C4) and nothing here needs them.
 *
 * `depth: "1"` returns only the DIRECT replies to `{id}`, which is how a
 * collapsed reply thread is expanded on demand — the same route, addressed at
 * the comment rather than at the post.
 */
export function getComments(
  postId: number,
  init: {
    depth?: "1" | "all";
    cursor?: string | null;
    limit?: number;
    ifChanged?: string | undefined;
  } = {}
): Promise<Pollable<Page<Comment>>> {
  return thetaApi.http
    .GET("/posts/{id}/comments", {
      params: {
        path: { id: postId },
        query: {
          ...(init.depth ? { depth: init.depth } : {}),
          ...(init.cursor ? { cursor: init.cursor } : {}),
          ...(init.ifChanged ? { ifChanged: init.ifChanged } : {}),
          // Clamped, never sent raw: `limit` maxes at 100 on every paged
          // route, and 101 is a 400 with no document rather than a slightly
          // short page. See `MAX_PAGE_LIMIT` in `lib/api/theta.ts`.
          ...(init.limit !== undefined ? { limit: pageLimit(init.limit) } : {}),
        },
      },
    })
    .then(pollablePageOf<Comment>);
}

/**
 * How many pages `getAllComments` will walk before it stops.
 *
 * A THREAD IS READ WHOLE, BUT NOT WITHOUT A CEILING. The thread read is
 * polled (1 Hz on the open post), so every page is re-fetched on every tick —
 * an unbounded walk would let one enormous thread turn a poll into a
 * request storm. Ten pages is 1,000 comments; the largest thread in the
 * corpus is two orders of magnitude short of that, and a thread that somehow
 * exceeded it renders its first 1,000 rather than failing.
 */
const MAX_THREAD_PAGES = 10;

/**
 * A whole comment thread, or a whole set of one comment's replies.
 *
 * ONE PAGE IS NOT A THREAD. `/posts/{id}/comments` is cursor-paged and caps
 * `limit` at 100 (§1.3/D4), so a 140-comment thread answered with `hasMore:
 * true` and a `nextCursor` — and a caller that read `page.items` and dropped
 * both rendered 100 comments under a card that said "140 kommentarer", with
 * no control anywhere to reach the other 40.
 *
 * Walking the cursor here rather than in the hook keeps the ordering property
 * intact: the route is DEPTH-FIRST, so concatenating consecutive pages is the
 * same sequence a single larger page would have been, and the caller still
 * indents by `depth` without rebuilding a tree.
 */
export async function getAllComments(
  postId: number,
  init: { depth?: "1" | "all"; ifChanged?: string | undefined } = {}
): Promise<Pollable<Comment[]>> {
  const items: Comment[] = [];
  let cursor: string | null = null;
  /**
   * THE TOKEN COMES FROM THE FIRST PAGE, AND SO DOES THE WHOLE SAVING.
   *
   * The route's state token describes the entire subtree — not the page it is
   * published with — so a hit on page 1 is a statement about pages 2..n as
   * well. That is why `ifChanged` is sent ONCE, at the top: an unchanged
   * thread costs one fingerprint query and returns here immediately, instead
   * of three full page requests a second for a 250-comment discussion.
   *
   * The later pages send no token deliberately. Each would have its own (the
   * cursor is hashed into it) and a hit on one of them would leave a gap in
   * the middle of the list this function is concatenating.
   */
  let state: string | null = null;
  for (let page = 0; page < MAX_THREAD_PAGES; page++) {
    const result = await getComments(postId, {
      ...(init.depth ? { depth: init.depth } : {}),
      limit: MAX_PAGE_LIMIT,
      ...(cursor ? { cursor } : {}),
      ...(page === 0 && init.ifChanged ? { ifChanged: init.ifChanged } : {}),
    });
    if (result.unchanged) {
      return { unchanged: true, state: result.state, pollAfterMs: result.pollAfterMs };
    }
    if (page === 0) state = result.state;
    items.push(...result.data.items);
    if (!result.data.hasMore || !result.data.nextCursor) break;
    cursor = result.data.nextCursor;
  }
  return { unchanged: false, data: items, state, pollAfterMs: null };
}

/**
 * Comment on a post, or reply to a comment.
 *
 * THE PARENT IS THE URL. Post to a post's id for a top-level comment, to a
 * comment's id for a reply to it. `reply_to_post_number` is gone and so is the
 * numbering behind it (C4) — which is why there is no `parentId` field here to
 * get out of step with the path.
 */
export function createComment(
  parentId: number,
  body: CreateCommentInput
): Promise<Comment> {
  return thetaApi.http
    .POST("/posts/{id}/comments", {
      params: { path: { id: parentId } },
      body,
    })
    .then(dataOf<Comment>);
}

/**
 * Vote in a poll. POLLS ARE ROWS NOW (§1.12) — a poll has an `id` and its
 * options have `digest`s, so a vote addresses them directly instead of naming
 * a `[poll name="…"]` block that had to be parsed back out of the body.
 *
 * The response is the COMPLETE poll, including this viewer's `myVotes` and
 * every option's `voteCount`. Splice it in; there is nothing to recompute.
 */
export function votePoll(
  postId: number,
  pollId: number,
  optionDigests: string[]
): Promise<Poll> {
  return thetaApi.http
    .PUT("/posts/{id}/polls/{pollId}/vote", {
      params: { path: { id: postId, pollId } },
      body: { optionDigests },
    })
    .then(dataOf<Poll>);
}

export function removePollVote(postId: number, pollId: number): Promise<Poll> {
  return thetaApi.http
    .DELETE("/posts/{id}/polls/{pollId}/vote", {
      params: { path: { id: postId, pollId } },
    })
    .then(dataOf<Poll>);
}
